import {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "@scott/ui/client";
import type { ReactNode } from "react";

const MAX_VISIBLE_PAGES = 5;
const PAGES_AROUND_CURRENT = 2; // Number of pages to show on each side of current page

export type PaginationItemData = {
  type: "page" | "ellipsis";
  value?: number;
};

export function generatePaginationItems(
  currentPage: number,
  totalPages: number
): PaginationItemData[] {
  const pages: PaginationItemData[] = [];

  // If total pages is small enough, show all pages
  if (totalPages <= MAX_VISIBLE_PAGES) {
    for (let i = 1; i <= totalPages; i++) {
      pages.push({ type: "page", value: i });
    }
    return pages;
  }

  // Calculate the range of pages to show around the current page
  let startPage = Math.max(1, currentPage - PAGES_AROUND_CURRENT);
  let endPage = Math.min(totalPages, currentPage + PAGES_AROUND_CURRENT);

  // Adjust the range to always show MAX_VISIBLE_PAGES pages when possible
  const rangeSize = endPage - startPage + 1;
  if (rangeSize < MAX_VISIBLE_PAGES) {
    if (startPage === 1) {
      endPage = Math.min(totalPages, startPage + MAX_VISIBLE_PAGES - 1);
    } else if (endPage === totalPages) {
      startPage = Math.max(1, endPage - MAX_VISIBLE_PAGES + 1);
    }
  }

  // Add first page and ellipsis if needed
  if (startPage > 1) {
    pages.push({ type: "page", value: 1 });
    if (startPage > 2) {
      pages.push({ type: "ellipsis" });
    }
  }

  // Add the range of pages around current page
  for (let i = startPage; i <= endPage; i++) {
    pages.push({ type: "page", value: i });
  }

  // Add ellipsis and last page if needed
  if (endPage < totalPages) {
    if (endPage < totalPages - 1) {
      pages.push({ type: "ellipsis" });
    }
    pages.push({ type: "page", value: totalPages });
  }

  return pages;
}

type PaginationControlsProps = {
  currentPage: number;
  totalPages: number;
  isFirstPage: boolean;
  isLastPage: boolean;
  onPageChange: (page: number) => void;
  onPrevious: () => void;
  onNext: () => void;
};

export function PaginationControls(
  props: Readonly<PaginationControlsProps>
): ReactNode {
  return (
    <Pagination className="my-4">
      <PaginationContent>
        <PaginationItem>
          <PaginationPrevious
            aria-disabled={props.isFirstPage}
            className={
              props.isFirstPage ? "pointer-events-none opacity-50" : ""
            }
            href="#"
            onClick={(e) => {
              e.preventDefault();
              props.onPrevious();
            }}
          />
        </PaginationItem>

        {generatePaginationItems(props.currentPage, props.totalPages).map(
          (item, index) => {
            if (item.type === "ellipsis") {
              return (
                <PaginationItem key={`ellipsis-${index}-${props.currentPage}`}>
                  <PaginationEllipsis />
                </PaginationItem>
              );
            }

            return (
              <PaginationItem key={item.value}>
                <PaginationLink
                  href="#"
                  isActive={item.value === props.currentPage}
                  onClick={(e) => {
                    e.preventDefault();
                    if (item.value) {
                      props.onPageChange(item.value);
                    }
                  }}
                >
                  {item.value}
                </PaginationLink>
              </PaginationItem>
            );
          }
        )}

        <PaginationItem>
          <PaginationNext
            aria-disabled={props.isLastPage}
            className={props.isLastPage ? "pointer-events-none opacity-50" : ""}
            href="#"
            onClick={(e) => {
              e.preventDefault();
              props.onNext();
            }}
          />
        </PaginationItem>
      </PaginationContent>
    </Pagination>
  );
}
